import {
  Alert,
  AppBar,
  Box,
  CircularProgress,
  Menu,
  MenuItem,
  Toolbar
} from "@mui/material";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import {
  selectAdminAuthority,
  selectCurrentUser,
  selectUserRoles
} from "../Users/usersSlice";
import { ADMIN_ROLES, Role } from "../Auth/roles";
import {
  canUseCoaching,
  canUseRotations,
  isRotationDirectorOnly
} from "../Auth/adminAccess";
import { handleSignOut } from "../../common-ui/SignOutButton";
import {
  setModalBoxStyle,
  setModalContent,
  setModalTitle,
  setShowModal
} from "../../common-ui/modalSlice";
import { useDispatch } from "react-redux";
import { ClassicReportsDeprecationModal } from "./ClassicReportsDeprecationModal";
import { useContext, useEffect, useState } from "react";
import { BrandingContext } from "../../common-ui/BrandingContext";
import {
  selectFeatureFlagsError,
  selectFeatureFlagsStatus
} from "../Firebase/firebaseSlice";
import { useNetworkProgramsByPrograms } from "../Networks/networkUtils";
import { useLoadNetworkFeatures } from "../Networks/networkFeaturesUtils";
import { selectAggregateFeatures } from "../Networks/networkFeaturesSlice";
import {
  createDashboardLinkText,
  getActiveProgramAdminedProgramIds,
  hasActiveTraineeOrAttendingMembership
} from "../../app/utils";

const STATIC_REPORTS_URL = "https://reports.simpl.org/";

type PageDefinition = {
  name: string;
  href: string;
};

const toggleLanguage = () => {
  const currentLanguage = i18next.language;
  const targetLanguage = currentLanguage === "en" ? "es" : "en";
  i18next.changeLanguage(targetLanguage, (err) => {
    if (err) {
      return console.error("error:", err);
    }
  });
};

export const Layout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUserRoles = useAppSelector(selectUserRoles);
  const currentUser = useAppSelector(selectCurrentUser);
  const adminAuthority = useAppSelector(selectAdminAuthority);

  const featureFlagsStatus = useAppSelector(selectFeatureFlagsStatus);
  const featureFlagsError = useAppSelector(selectFeatureFlagsError);
  const {
    networkPrograms,
    status: networkProgramsStatus,
    error: networkProgramsError,
    getNetworkProgramsByPrograms
  } = useNetworkProgramsByPrograms();

  const [haveNecessaryNetworkPrograms, setHaveNecessaryNetworkPrograms] =
    useState(false);
  const [dashboardLinks, setDashboardLinks] = useState<PageDefinition[]>([]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const branding = useContext(BrandingContext);
  const [assessmentsAnchorEl, setAssessmentsAnchorEl] =
    useState<null | HTMLElement>(null);
  const isAdmin = currentUserRoles?.some((role) => ADMIN_ROLES.includes(role));
  const isSysAdmin = currentUserRoles?.includes(Role.SystemAdmin);
  const isProgramAdmin = currentUserRoles?.includes(Role.ProgramAdmin);
  // The Assessments menu is shown for users who have an active Trainee or
  // Attending program membership (matches the mobile clients' gate). Admin
  // memberships alone do not unlock it — a SysAdmin without any Trainee /
  // Attending membership will not see this nav item, mirroring the
  // pre-rework Cognito `Users`-group behavior.
  const canAssess = hasActiveTraineeOrAttendingMembership(currentUser);

  // SE-1765: per-network feature flags. Populated once per authenticated
  // session; drives the Coaching / Rotations tab visibility below.
  useLoadNetworkFeatures(!!currentUser);
  const networkFeatures = useAppSelector(selectAggregateFeatures);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAssessmentsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAssessmentsAnchorEl(event.currentTarget);
  };

  const handleAssessmentClose = () => {
    setAssessmentsAnchorEl(null);
  };

  let adminPages: PageDefinition[] = [
    { name: "Users", href: "/users" },
  ];
  // SE-1765 T13: Coaching and Rotations tabs are ABP-only (network
  // feature flags `coaching` / `rotations`) and authority-gated: Coaching
  // for System / Program Admins, Rotations also for Rotation Directors.
  // The routes and the Lambdas enforce the same rules.
  const showCoaching = networkFeatures.coaching && canUseCoaching(adminAuthority);
  const showRotations =
    networkFeatures.rotations && canUseRotations(adminAuthority);
  if (showCoaching) {
    adminPages.push({ name: "Coaching", href: "/coaching" });
  }
  if (showRotations) {
    adminPages.push({ name: "Rotations", href: "/rotations" });
  }
  // A Rotation Director who isn't an admin gets only their Rotations tab.
  const directorOnlyPages: PageDefinition[] =
    !isAdmin && showRotations && isRotationDirectorOnly(adminAuthority)
      ? [{ name: "Rotations", href: "/rotations" }]
      : [];

  const userPages: PageDefinition[] = [
    { name: "Pending Assessments", href: "/assessments/pending" },
    { name: "Create New Assessment", href: "/assessments/create" }
  ];

  if (isSysAdmin) {
    adminPages = [
      ...adminPages,
      { name: "Programs", href: "/programs" },
      { name: "Networks", href: "/networks" },
      { name: "Institutions", href: "/institutions" },
      { name: "Assessment Definitions", href: "/assessmentdefinitions" }
    ];
  }

  useEffect(() => {
    if (currentUser) {
      const activeProgramAdminedProgramIds =
        getActiveProgramAdminedProgramIds(currentUser);
      if (
        activeProgramAdminedProgramIds &&
        activeProgramAdminedProgramIds.length > 0
      ) {
        getNetworkProgramsByPrograms(activeProgramAdminedProgramIds);
      } else {
        setHaveNecessaryNetworkPrograms(true);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    if (networkProgramsStatus === "succeeded") {
      setHaveNecessaryNetworkPrograms(true);
    }
  }, [networkProgramsStatus]);

  useEffect(() => {
    if (haveNecessaryNetworkPrograms && networkPrograms) {
      const seenNames = new Set<string>();
      const dashboards = networkPrograms
        .map((networkProgram) => {
          const nameToUse =
            networkProgram.network.name || networkProgram.network.sponsor?.name;

          if (nameToUse) {
            return {
              name: createDashboardLinkText(nameToUse),
              href: STATIC_REPORTS_URL
            };
          }
        })
        .flatMap((dashboard) => (dashboard ? [dashboard] : []))
        .filter((dashboard) => {
          // Keep only first occurrence of each name
          if (seenNames.has(dashboard.name)) return false;
          seenNames.add(dashboard.name);
          return true;
        });
      setDashboardLinks(dashboards);
    }
  }, [haveNecessaryNetworkPrograms, networkPrograms]);

  // Show loading spinner only for initial feature flags load
  // Don't block on network programs - they can load in background
  if (featureFlagsStatus === "idle" || featureFlagsStatus === "loading") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh"
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (featureFlagsStatus === "failed") {
    return <Alert severity="error">{featureFlagsError}</Alert>;
  }

  // Show the layout even if network programs are still loading
  // Individual components can handle their own loading states
  // Network programs will update the Reports menu dynamically when ready
  return (
    <>
      <AppBar
        data-testid="app-bar"
        position="static"
        sx={{
          "&& .Mui-selected": {
            backgroundColor: "white",
            color: "black",
            ":hover": {
              backgroundColor: "rgba(255,255,255,0.8)"
            }
          }
        }}
      >
        <Toolbar sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
          <MenuItem onClick={() => navigate("/")} sx={{ height: 64, mr: 2 }}>
            <Box
              component="img"
              alt={`${branding.name} logo`}
              src={branding.logoFullMonoURL}
              sx={{
                height: "100%",
                width: "auto"
              }}
            />
          </MenuItem>

            {canAssess && (
              <>
                <MenuItem
                  sx={{ ml: 0.5, mr: 0.5 }}
                  onClick={handleAssessmentsMenu}
                  selected={location.pathname.includes("/assessments")}
                >
                  {t("Assessments")}
                </MenuItem>
                <Menu
                  id="assessments-appbar"
                  anchorEl={assessmentsAnchorEl}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left"
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left"
                  }}
                  open={Boolean(assessmentsAnchorEl)}
                  onClose={handleAssessmentClose}
                >
                  {userPages.map((page) => (
                    <MenuItem
                      key={page.name}
                      onClick={handleAssessmentClose}
                      component={Link}
                      selected={location.pathname.includes(page.href)}
                      to={page.href}
                      sx={{ ml: 0.5, mr: 0.5 }}
                    >
                      {t(page.name)}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
            {directorOnlyPages.map((page) => (
              <MenuItem
                key={page.name}
                component={Link}
                selected={location.pathname.includes(page.href)}
                to={page.href}
                sx={{ ml: 0.5, mr: 0.5 }}
              >
                {t(page.name)}
              </MenuItem>
            ))}
            {isAdmin && (
              <>
                {adminPages.map((page, index) => (
                  <MenuItem
                    key={`${index}-${page.name}`}
                    component={Link}
                    selected={location.pathname.includes(page.href)}
                    to={page.href}
                    sx={{ ml: 0.5, mr: 0.5 }}
                  >
                    {t(page.name)}
                  </MenuItem>
                ))}
                {
                  // Reports + Program Assessments + Module Builder block.
                  // Shown when user has dashboard links, is SysAdmin, or is Program Admin.
                  (dashboardLinks.length > 0 || isSysAdmin || isProgramAdmin) && (
                    <>
                      {dashboardLinks.length === 0 ? (
                        // No per-network dashboard links: one-click Reports opens the
                        // deprecation modal directly (no submenu).
                        <MenuItem
                          sx={{ ml: 0.5, mr: 0.5 }}
                          onClick={() => {
                            dispatch(setModalContent(<ClassicReportsDeprecationModal />));
                            dispatch(
                              setModalBoxStyle({
                                width: "auto",
                                minWidth: 360,
                                maxWidth: 480
                              })
                            );
                            dispatch(setModalTitle("Reports"));
                            dispatch(setShowModal(true));
                          }}
                          data-testid="reports-menu"
                        >
                          {t("Reports")}
                        </MenuItem>
                      ) : (
                        // Has per-network dashboard links: keep the dropdown so those
                        // external links remain accessible from the Reports nav.
                        <>
                          <MenuItem
                            sx={{ ml: 0.5, mr: 0.5 }}
                            onClick={handleMenu}
                            selected={location.pathname.includes("/reports")}
                            data-testid="reports-menu"
                          >
                            {t("Reports")}
                          </MenuItem>
                          <Menu
                            id="reports-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "left"
                            }}
                            keepMounted
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "left"
                            }}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                          >
                            {dashboardLinks.map((page, index) => (
                              <MenuItem
                                key={`${index}-${page.name}`}
                                onClick={handleClose}
                                component="a"
                                href={page.href}
                                target="_blank"
                                rel="noopener"
                              >
                                {t(page.name)}
                              </MenuItem>
                            ))}
                          </Menu>
                        </>
                      )}
                      <MenuItem
                        component={Link}
                        to="/program-assessments"
                        selected={location.pathname.includes(
                          "/program-assessments"
                        )}
                        sx={{ ml: 0.5, mr: 0.5 }}
                        data-testid="program-assessments-nav"
                      >
                        {t("Program Assessments")}
                      </MenuItem>
                      {isSysAdmin && (
                        <MenuItem
                          component={Link}
                          to="/module-builder"
                          selected={location.pathname === "/module-builder"}
                          sx={{ ml: 0.5, mr: 0.5 }}
                          data-testid="module-builder-nav"
                        >
                          {t("Module builder")}
                        </MenuItem>
                      )}
                    </>
                  )
                }
              </>
            )}
            <MenuItem
              onClick={() => handleSignOut({ dispatch })}
              sx={{ ml: "auto" }}
            >
              {t("Sign Out")}
            </MenuItem>
          </Toolbar>
        </AppBar>
        <Outlet />
      </>
    );
};
